import Note from '@/components/Note'
import WebPreview from '@/components/WebPreview'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { transformCustomEmojisInContent, trimTagEnd } from '@/lib/draft-event'
import { addPendingPublish, removePendingPublish, usePendingCountdown } from '@/lib/pendingPublish'
import { isTouchDevice, now } from '@/lib/utils'
import {
  getReplaceableCoordinateFromEvent,
  getRoot,
  isReplaceableEvent,
  isReplyNoteEvent
} from '@/lib/event'
import { addReplies } from '@/atoms/replies'
import { canWriteAtom, unwrappedSignerAtom, useCurrentPubkey } from '@/atoms/session'
import { hasUnsavedNsec, openSaveNsecModal, recordNotePublishedDay } from '@/atoms/key-backup'
import client from '@/services/client.service'
import { discardPendingEvent, savePendingEvent } from '@/atoms/pending'

import { HugeiconsIcon } from '@hugeicons/react'
import {
  EyeOffIcon,
  ImageUpload01Icon,
  Loading01Icon,
  SmileIcon,
  SlowWindsIcon,
  ShieldMinus,
  CrownIcon
} from '@hugeicons/core-free-icons'
import { NostrEvent } from '@nostr/tools/core'
import * as kinds from '@nostr/tools/kinds'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import EmojiPickerDialog from '../EmojiPickerDialog'
import PostTextarea, { TPostTextareaHandle } from './PostTextarea'
import Uploader from './Uploader'
import BlossomUploadChecklist from './BlossomUploadChecklist'
import {
  getImetaTagByUrl,
  type ServerStatus,
  type UploadFile
} from '@/services/media-upload.service'
import RelaySelector, { Item, SelectableItem } from '../RelaySelector'
import { simplifyUrl } from '@/lib/url'
import { parse } from '@nostr/tools/nip27'
import { unique } from '@nostr/gadgets/utils'
import { invalidateFeed, invalidateReplies } from '@/atoms/invalidation'
import { unwrappedFavoriteRelaysWithSetsAtom, unwrappedRelayListAtom } from '@/atoms/lists'
import { useAtom, useAtomValue } from 'jotai'
import { getComposerStateAtom, removeComposerState, TComposerState } from './composer-state'
import { useAsync } from 'react-use'
import { loadRelayInfo } from '@nostr/gadgets/relays'
import { unwrappedIsPremiumPublisherAtom } from '@/atoms/premium'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import SchedulePicker, { ScheduleButton } from './SchedulePicker'

const PENDING_PUBLISH_MS = 10_000

function waitForAbortableDelay(duration: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort)
      resolve()
    }, duration)

    const handleAbort = () => {
      window.clearTimeout(timeout)
      signal.removeEventListener('abort', handleAbort)
      reject(new DOMException('Aborted', 'AbortError'))
    }

    signal.addEventListener('abort', handleAbort)
  })
}

function PendingPublishToast({ endAt, onUndo }: { endAt: number; onUndo: () => void }) {
  const secondsLeft = usePendingCountdown(endAt)
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-3 rounded-full bg-primary text-primary-foreground shadow hover:bg-primary-hover py-2 pl-4 pr-3">
      <div className="text-sm font-medium">{t('Changed your mind?')}</div>
      <div className="text-sm font-medium w-8 text-center">{secondsLeft}s</div>
      <Button variant="secondary" size="sm" onClick={onUndo}>
        {t('Cancel')}
      </Button>
    </div>
  )
}

export default function PostContent({
  defaultContent = '',
  editingEvent,
  parentEvents,
  parentExternal,
  close,
  open,
  openFromRelays,
  initialState
}: {
  defaultContent?: string
  editingEvent?: NostrEvent
  parentEvents?: NostrEvent[]
  parentExternal?: string
  close: () => void
  open: (content?: string, initialState?: TComposerState) => void
  openFromRelays?: string[]
  initialState?: TComposerState
}) {
  const { t } = useTranslation()
  const canWrite = useAtomValue(canWriteAtom)
  const signer = useAtomValue(unwrappedSignerAtom)
  const pubkey = useCurrentPubkey()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [text, setText] = useState('')
  const textareaRef = useRef<TPostTextareaHandle>(null)
  const scheduleAnchorRef = useRef<HTMLButtonElement>(null)
  const [posting, setPosting] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [uploadProgresses, setUploadProgresses] = useState<
    { file: UploadFile; cancel: () => void; servers: { url: string; status: ServerStatus }[] }[]
  >([])

  const seenOn =
    parentEvents && parentEvents.every((pe) => pe.tags.some(([t]) => t === '-'))
      ? parentEvents.flatMap((pe) => client.getSeenEventRelayUrls(pe.id, pe))
      : []

  const publishTargetItems = (window.hallway.universe.publishTargets ?? []).map(
    (url) => ({ kind: 'relay', url }) as const
  )
  const ourWriteRelaysInDefault = !openFromRelays?.length && seenOn.length === 0

  const composerStateAtom = getComposerStateAtom(
    defaultContent,
    parentEvents,
    openFromRelays,
    () => {
      const base: SelectableItem[] = openFromRelays?.length
        ? openFromRelays.map((url) => ({ kind: 'relay', url }))
        : seenOn.length
          ? seenOn.map((url) => ({ kind: 'relay', url }))
          : window.hallway.universe.publishTargetsExclusive
            ? []
            : parentEvents
              ? ['ourWriteRelays', 'theirReadRelays'].map((id) => ({
                  kind: 'item',
                  id,
                  label: ''
                }))
              : [{ kind: 'item', id: 'ourWriteRelays', label: '' }]

      return {
        nsfw: false,
        protected: window.hallway.universe.protectedDefault,
        premium: false,
        content: null,
        quietReply: false,
        relaySelection: ourWriteRelaysInDefault ? [...base, ...publishTargetItems] : base
      }
    },
    initialState
  )

  const [composerState, setComposerState] = useAtom(composerStateAtom)
  const [scheduledAt, setScheduledAt] = useState<number | null>(null)

  const hasSpecificRelays = composerState.relaySelection.some((item) => item.kind !== 'item')
  const hasOurWriteRelays = composerState.relaySelection.some(
    (item) => item.kind === 'item' && item.id === 'ourWriteRelays'
  )
  const isSpecificOnly = hasSpecificRelays && !hasOurWriteRelays
  const wasSpecificOnlyRef = useRef(false)

  useEffect(() => {
    if (isSpecificOnly && !wasSpecificOnlyRef.current) {
      setComposerState((curr) => (curr.protected ? curr : { ...curr, protected: true }))
    }
    wasSpecificOnlyRef.current = isSpecificOnly
  }, [isSpecificOnly, setComposerState])

  useEffect(() => {
    if (composerState.premium && !composerState.protected) {
      setComposerState((curr) => ({ ...curr, protected: true }))
    }
  }, [composerState.premium, composerState.protected, setComposerState])

  const canPost =
    !!pubkey &&
    canWrite &&
    !!text &&
    !posting &&
    !uploadProgresses.length &&
    (!composerState.protected || hasSpecificRelays) &&
    (!composerState.premium || hasSpecificRelays) &&
    (scheduledAt == null ||
      composerState.relaySelection.some(
        (item) => item.kind === 'relay' || item.kind === 'relay-set'
      ))

  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)

  const writeRelays = (useAtomValue(unwrappedRelayListAtom) ?? []).filter((r) => r.write)
  const writeRelaysKey = writeRelays.map((r) => r.url).join('|')

  const { value: nip16WriteRelays } = useAsync(async (): Promise<string[]> => {
    if (writeRelays.length === 0) return []
    const infos = await Promise.all(writeRelays.map((r) => loadRelayInfo(r.url)))
    return writeRelays.filter((_, i) => infos[i]?.supported_nips?.includes?.(16)).map((r) => r.url)
  }, [writeRelaysKey])

  const favoriteRelayUrls = useMemo(() => {
    if (!favorites) return []
    const urls: string[] = []
    for (const f of favorites) {
      if (typeof f === 'string') urls.push(f)
      else urls.push(...f.items)
    }
    return unique(urls)
  }, [favorites])
  const favoriteRelayUrlsKey = favoriteRelayUrls.join('|')

  const { value: nip63WriteRelays } = useAsync(async (): Promise<string[]> => {
    if (writeRelays.length === 0) return []
    const infos = await Promise.all(writeRelays.map((r) => loadRelayInfo(r.url)))
    return writeRelays.filter((_, i) => infos[i]?.supported_nips?.includes?.(63)).map((r) => r.url)
  }, [writeRelaysKey])

  const { value: nip63FavoriteRelays } = useAsync(async (): Promise<string[]> => {
    if (favoriteRelayUrls.length === 0) return []
    const infos = await Promise.all(favoriteRelayUrls.map((url) => loadRelayInfo(url)))
    return favoriteRelayUrls.filter((_, i) => infos[i]?.supported_nips?.includes?.(63))
  }, [favoriteRelayUrlsKey])

  const isPremiumPublisher = useAtomValue(unwrappedIsPremiumPublisherAtom)

  const nip16Set = useMemo(() => new Set(nip16WriteRelays ?? []), [nip16WriteRelays])
  const isScheduled = scheduledAt != null
  const hasNip16Support = nip16Set.size > 0

  const hasExplicitMentions = useMemo(() => {
    if (parentEvents) return false
    for (const block of parse(text)) {
      if (block.type === 'reference') {
        if ('pubkey' in block.pointer && block.pointer.pubkey === pubkey) continue
        if ('author' in block.pointer && block.pointer.author === pubkey) continue
        return true
      }
    }
    return false
  }, [text, parentEvents])

  useEffect(() => {
    if (isScheduled) return
    setComposerState((curr) => {
      const hasTheirReadRelays = curr.relaySelection.some(
        (item) => item.kind === 'item' && item.id === 'theirReadRelays'
      )
      if (hasExplicitMentions && !hasTheirReadRelays) {
        return {
          ...curr,
          relaySelection: [
            ...curr.relaySelection,
            { kind: 'item', id: 'theirReadRelays', label: '' }
          ]
        }
      }
      if (!hasExplicitMentions && hasTheirReadRelays && !parentEvents) {
        return {
          ...curr,
          relaySelection: curr.relaySelection.filter(
            (item) => !(item.kind === 'item' && item.id === 'theirReadRelays')
          )
        }
      }
      return curr
    })
  }, [hasExplicitMentions, isScheduled, parentEvents, setComposerState])

  const initialSelected = useMemo(() => {
    const ids: string[] = []
    for (const item of composerState.relaySelection) {
      if (item.kind === 'relay') ids.push(item.url)
      else if (item.kind === 'item') ids.push(item.id)
      else if (item.kind === 'relay-set') ids.push(item.identifier)
    }
    return new Set(ids)
  }, [composerState.relaySelection])

  useEffect(() => {
    if (!isScheduled) return
    if (nip16Set.size === 0) return
    setComposerState((curr) => {
      const next: SelectableItem[] = []
      let hasOurWriteRelays = false
      let hasTheirReadRelays = false
      const explicit: string[] = []
      for (const item of curr.relaySelection) {
        if (item.kind === 'item' && item.id === 'ourWriteRelays') {
          hasOurWriteRelays = true
          continue
        }
        if (item.kind === 'item' && item.id === 'theirReadRelays') {
          hasTheirReadRelays = true
          continue
        }
        if (item.kind === 'relay') {
          if (nip16Set.has(item.url)) {
            if (!explicit.includes(item.url)) {
              explicit.push(item.url)
              next.push(item)
            }
          }
          continue
        }
        if (item.kind === 'relay-set') {
          const filtered = item.relays.filter((u) => nip16Set.has(u))
          if (filtered.length) {
            next.push({ ...item, relays: filtered })
          }
          continue
        }
      }
      if (hasOurWriteRelays || (explicit.length === 0 && !hasTheirReadRelays)) {
        for (const url of nip16Set) {
          if (!explicit.includes(url)) {
            explicit.push(url)
            next.push({ kind: 'relay', url })
          }
        }
      }
      if (
        next.length === curr.relaySelection.length &&
        next.every((n, i) => {
          const c = curr.relaySelection[i]
          if (!c) return false
          if (n.kind !== c.kind) return false
          if (n.kind === 'relay' && c.kind === 'relay') return n.url === c.url
          if (n.kind === 'relay-set' && c.kind === 'relay-set') {
            return n.identifier === c.identifier && n.relays.length === c.relays.length
          }
          return false
        })
      ) {
        return curr
      }
      return { ...curr, relaySelection: next }
    })
  }, [isScheduled, nip16Set, setComposerState])

  const relayOptions = useMemo(() => {
    if (isScheduled && nip16Set.size > 0) {
      const opts: Item[] = []
      const explicit = Array.from(nip16Set)
      if (explicit.length) {
        opts.push(...explicit.map((url) => ({ kind: 'relay' as const, url })))
      }
      return opts
    }

    const opts: Item[] = [{ kind: 'item', id: 'ourWriteRelays', label: t('Our outbox relays') }]
    const added: string[] = publishTargetItems.map((r) => r.url)

    if (parentEvents || hasExplicitMentions) {
      opts.push({ kind: 'item', id: 'theirReadRelays', label: t('Their inbox relays') })
    }

    {
      // favorites section
      opts.push({ kind: 'separator' })
      if (openFromRelays)
        for (const url of openFromRelays) {
          if (added.includes(url)) continue
          opts.push({
            kind: 'relay',
            url
          })
          added.push(url)
        }
      for (const url of seenOn) {
        if (added.includes(url)) continue
        opts.push({
          kind: 'relay',
          url
        })
        added.push(url)
      }
      if (favorites) {
        for (const fav of favorites) {
          if (typeof fav === 'string') {
            if (added.includes(fav)) continue
            opts.push({
              kind: 'relay',
              url: fav
            })
            added.push(fav)
          } else {
            opts.push({
              kind: 'relay-set',
              identifier: fav.pointer.identifier,
              relays: fav.items,
              name: fav.title
            })
          }
        }
      }

      // if nothing was added remove the separator
      if (opts[opts.length - 1].kind === 'separator') opts.pop()
    }

    if (writeRelays.length) {
      // write relays section
      opts.push({ kind: 'separator' })

      for (const r of writeRelays) {
        if (added.includes(r.url)) continue
        opts.push({
          kind: 'relay',
          url: r.url
        })
        added.push(r.url)
      }

      // if nothing was added remove the separator
      if (opts[opts.length - 1].kind === 'separator') opts.pop()
    }

    if (publishTargetItems.length) {
      // hardcoded target sections
      opts.push({ kind: 'separator' })
      for (const t of publishTargetItems) opts.push(t)

      // this will always be added because we forcelly prevent the same relays from being added before
      // by preemptively adding these to the `added` array
    }

    return opts
  }, [
    parentEvents,
    hasExplicitMentions,
    favorites,
    openFromRelays,
    isScheduled,
    nip16Set,
    writeRelays
  ])

  const relaySelectionDescription = useMemo(() => {
    if (composerState.relaySelection.length === 0) {
      return t('No relays selected')
    }
    if (composerState.relaySelection.length === 1) {
      const item = composerState.relaySelection[0]
      if (item.kind === 'item') {
        if (item.id === 'ourWriteRelays') {
          return t('Our write relays')
        }
        if (item.id === 'theirReadRelays') {
          return t('Their read relays')
        }
      }

      if (item.kind === 'relay') {
        return simplifyUrl(item.url)
      }

      if (item.kind === 'relay-set') {
        return item.relays.length > 1
          ? t('{{count}} relays', { count: item.relays.length })
          : simplifyUrl(item.relays[0])
      }
    }

    const hasOurWriteRelays = composerState.relaySelection.some(
      (item) => item.kind === 'item' && item.id === 'ourWriteRelays'
    )
    const hasTheirReadRelays = composerState.relaySelection.some(
      (item) =>
        (item.kind === 'item' && item.id === 'theirReadRelays') ||
        (item.kind === 'relay' && item.url === 'theirReadRelays')
    )
    const relayCount = composerState.relaySelection.reduce((count, item) => {
      if (item.kind === 'relay') {
        return count + 1
      }
      if (item.kind === 'relay-set') {
        return count + item.relays.length
      }
      return count
    }, 0)
    if (hasOurWriteRelays && hasTheirReadRelays) {
      return relayCount > 0
        ? t('Our outbox relays, their inbox relays and {{count}} other relays', {
            count: relayCount
          })
        : t('Our outbox relays and inbox read relays')
    }
    if (hasOurWriteRelays) {
      return relayCount > 0
        ? t('Our outbox relays and {{count}} other relays', { count: relayCount })
        : t('Our outbox relays')
    }
    if (hasTheirReadRelays) {
      return relayCount > 0
        ? t('Their inbox relays and {{count}} other relays', { count: relayCount })
        : t('Their inbox relays')
    }
    return t('{{count}} relays', { count: relayCount })
  }, [composerState.relaySelection, t])

  return (
    <div className="space-y-2">
      {parentEvents && parentEvents.length > 1 ? (
        <ScrollArea className="flex max-h-48 flex-col overflow-y-auto rounded-lg border bg-muted/40">
          {parentEvents.map((evt) => (
            <div key={evt.id} className="p-2 sm:p-3 pointer-events-none border-b last:border-none">
              <Note size="small" event={evt} hideParentNotePreview showClientTag />
            </div>
          ))}
        </ScrollArea>
      ) : parentEvents && parentEvents.length === 1 ? (
        <ScrollArea className="flex max-h-48 flex-col overflow-y-auto rounded-lg border bg-muted/40">
          <div className="p-2 sm:p-3 pointer-events-none">
            <Note size="small" event={parentEvents[0]} hideParentNotePreview showClientTag />
          </div>
        </ScrollArea>
      ) : parentExternal ? (
        <div className="pointer-events-none rounded-lg border bg-muted/40 overflow-hidden">
          <WebPreview url={parentExternal} />
        </div>
      ) : null}
      <PostTextarea
        ref={textareaRef}
        text={text}
        setText={setText}
        defaultContent={defaultContent}
        composerStateAtom={composerStateAtom}
        onSubmit={() => post()}
        className="min-h-52"
        onUploadStart={handleUploadStart}
        onUploadEnd={handleUploadEnd}
        onUploadInit={handleUploadInit}
        onServerStatus={handleServerStatus}
      />
      {uploadProgresses.length > 0 && (
        <div className="space-y-2">
          {uploadProgresses.map(({ file, cancel, servers }, index) => (
            <BlossomUploadChecklist
              key={`${file.name}-${index}`}
              fileName={file.name}
              servers={servers}
              onCancel={() => {
                cancel?.()
                handleUploadEnd(file)
              }}
            />
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        {isScheduled ? t('Schedule to') : t('Post to')}
        <RelaySelector
          selected={initialSelected}
          onChange={(rs) => setComposerState((curr) => ({ ...curr, relaySelection: rs }))}
          items={relayOptions}
          hideChangedDot
        >
          <Button type="button" variant="outline" className="px-2 flex-1 max-w-fit justify-start">
            {isScheduled
              ? composerState.relaySelection.length === 0
                ? t('No relays selected')
                : t('Schedule-capable relays')
              : relaySelectionDescription}
          </Button>
        </RelaySelector>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <Uploader
            onUploadSuccess={({ url }) => {
              textareaRef.current?.appendText(url, true)
            }}
            onUploadStart={handleUploadStart}
            onUploadEnd={handleUploadEnd}
            onUploadInit={handleUploadInit}
            onServerStatus={handleServerStatus}
            accept="image/*,video/*,audio/*"
          >
            <Button variant="ghost" size="icon">
              <HugeiconsIcon icon={ImageUpload01Icon} />
            </Button>
          </Uploader>
          {/* I'm not sure why, but after triggering the virtual keyboard,
              opening the emoji picker drawer causes an issue,
              the emoji I tap isn't the one that gets inserted. */}
          {!isTouchDevice() && (
            <EmojiPickerDialog
              onEmojiClick={(emoji) => {
                if (!emoji) return
                textareaRef.current?.insertEmoji(emoji)
              }}
            >
              <Button variant="ghost" size="icon">
                <HugeiconsIcon icon={SmileIcon} />
              </Button>
            </EmojiPickerDialog>
          )}
          <Button
            variant="ghost"
            size="icon"
            title={t('NSFW')}
            className={composerState.nsfw ? 'bg-primary/15 text-primary hover:bg-primary/15' : ''}
            onClick={() => setComposerState((curr) => ({ ...curr, nsfw: !curr.nsfw }))}
          >
            <HugeiconsIcon icon={EyeOffIcon} />
          </Button>
          {parentEvents && parentEvents.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              title={
                parentEvents[0].kind !== kinds.ShortTextNote ? t('Comment reply') : t('Quiet reply')
              }
              className={
                parentEvents[0].kind !== kinds.ShortTextNote ||
                parentEvents.length > 1 ||
                composerState.quietReply
                  ? 'bg-primary/15 text-primary hover:bg-primary/15'
                  : ''
              }
              onClick={() => {
                if (parentEvents[0].kind !== kinds.ShortTextNote || parentEvents.length > 1)
                  // when replying to anything other than a kind:1
                  // or when replying to multiple events at the same time
                  // force the "quiet reply"/comment
                  return

                setComposerState((curr) => ({ ...curr, quietReply: !curr.quietReply }))
              }}
            >
              <HugeiconsIcon icon={SlowWindsIcon} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            title={t('Protected (NIP-70)')}
            className={
              composerState.protected ? 'bg-primary/15 text-primary hover:bg-primary/15' : ''
            }
            onClick={() => setComposerState((curr) => ({ ...curr, protected: !curr.protected }))}
          >
            <HugeiconsIcon icon={ShieldMinus} />
          </Button>
          {isPremiumPublisher && (!parentEvents || parentEvents.length === 0) && (
            <Button
              variant="ghost"
              size="icon"
              title={t('Premium (NIP-63)')}
              className={
                composerState.premium
                  ? 'bg-yellow-400/15 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-400/15'
                  : ''
              }
              onClick={() => {
                if (composerState.premium) {
                  setComposerState((curr) => ({ ...curr, premium: false }))
                  return
                }
                const premiumRelays =
                  nip63WriteRelays && nip63WriteRelays.length > 0
                    ? nip63WriteRelays
                    : (nip63FavoriteRelays ?? [])
                setComposerState((curr) => ({
                  ...curr,
                  premium: true,
                  relaySelection: premiumRelays.map((url) => ({ kind: 'relay', url }))
                }))
              }}
            >
              <HugeiconsIcon icon={CrownIcon} />
            </Button>
          )}
          {hasNip16Support && (
            <ScheduleButton
              ref={scheduleAnchorRef}
              scheduledAt={scheduledAt}
              isActive={isScheduled}
              onClick={() => setScheduleOpen((v) => !v)}
            />
          )}
          {hasNip16Support && !isSmallScreen && (
            <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
              <PopoverAnchor virtualRef={scheduleAnchorRef} />
              <PopoverContent className="w-auto p-4" align="start" side="top" collisionPadding={16}>
                <SchedulePicker
                  scheduledAt={scheduledAt}
                  onChange={(ts) => {
                    setScheduledAt(ts)
                    if (ts != null) setScheduleOpen(false)
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
          {hasNip16Support && isSmallScreen && (
            <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
              <DialogContent
                className="w-fit max-w-[calc(100vw-2rem)] p-4"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <SchedulePicker
                  scheduledAt={scheduledAt}
                  onChange={(ts) => {
                    setScheduledAt(ts)
                    if (ts != null) setScheduleOpen(false)
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-2 items-center max-sm:hidden">
            <Button variant="secondary" disabled={!canPost || posting} onClick={saveForLater}>
              {editingEvent ? t('Save') : t('Save for later')}
            </Button>
            <Button
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation()
                close()
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!canPost}
              onClick={post}
              className={
                composerState.premium
                  ? 'bg-yellow-400 text-black hover:bg-yellow-500 border-yellow-500'
                  : ''
              }
            >
              {posting && <HugeiconsIcon icon={Loading01Icon} className="animate-spin" />}
              {isScheduled ? t('Schedule') : parentEvents ? t('Reply') : t('Post')}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:hidden">
        <div className="flex gap-2">
          <Button
            className="w-full"
            variant="secondary"
            disabled={!canPost || posting}
            onClick={saveForLater}
          >
            {editingEvent ? t('Save') : t('Save for later')}
          </Button>
          <Button
            className="w-full"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation()
              close()
            }}
          >
            {t('Cancel')}
          </Button>
        </div>
        <Button
          className={`w-full${composerState.premium ? ' bg-yellow-400 text-black hover:bg-yellow-500 border-yellow-500' : ''}`}
          type="submit"
          disabled={!canPost}
          onClick={post}
        >
          {posting && <HugeiconsIcon icon={Loading01Icon} className="animate-spin" />}
          {isScheduled ? t('Schedule') : parentEvents ? t('Reply') : t('Post')}
        </Button>
      </div>
    </div>
  )

  async function prepare(): Promise<[event: NostrEvent, relayUrls: string[]]> {
    const shouldPublishAsComment =
      !!parentEvents &&
      (parentEvents[0].kind !== kinds.ShortTextNote ||
        composerState.quietReply ||
        (parentEvents && parentEvents.length > 1))

    const tags: string[][] = []
    function addTag(tag: string[]) {
      for (let i = 0; i < tags.length; i++) {
        const t = tags[i]
        if (t[0] === tag[0] && t[1] === tag[1]) return
      }
      tags.push(tag)
    }

    const additionalMentionPubkeys: string[] = []
    function addMention(pk: string) {
      if (pk === pubkey || additionalMentionPubkeys.includes(pk)) return
      additionalMentionPubkeys.push(pk)
    }

    const { content, emojiTags } = transformCustomEmojisInContent(text)
    for (const emoji of emojiTags) addTag(emoji)

    for (const block of parse(content)) {
      switch (block.type) {
        case 'image':
          const tag = getImetaTagByUrl(block.url)
          if (tag) addTag(tag)
          break
        case 'hashtag':
          addTag(['t', block.value])
          break
        case 'reference':
          if ('identifier' in block.pointer) {
            addTag([
              'q',
              `${block.pointer.kind}:${block.pointer.pubkey}:${block.pointer.identifier}`
            ])
          } else if ('id' in block.pointer) {
            const url = client.getEventHint(block.pointer.id)
            addTag(['q', block.pointer.id, url])
          } else {
            if (block.pointer.pubkey !== pubkey) addTag(['p', block.pointer.pubkey])
            addMention(block.pointer.pubkey)
          }
          break
      }
    }

    if (parentEvents) {
      // root tags
      for (const pe of parentEvents) {
        const rootInfo = await getRoot(pe)

        if (shouldPublishAsComment) {
          // NIP-22
          if (rootInfo && 'identifier' in rootInfo) {
            addTag([
              'A',
              `${rootInfo.kind}:${rootInfo.pubkey}:${rootInfo.identifier}`,
              ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : [])
            ])
            addTag(['K', `${rootInfo.kind}`])
            addTag(['P', rootInfo.pubkey])
          } else if (rootInfo && 'id' in rootInfo) {
            addTag(['E', rootInfo.id, ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : [])])
            if (rootInfo.kind) addTag(['K', `${rootInfo.kind}`])
            if (rootInfo.author) addTag(['P', rootInfo.author])
          } else if (rootInfo && 'value' in rootInfo) {
            addTag(['I', rootInfo.value])
            if (rootInfo.value.startsWith('http')) addTag(['K', 'web'])
          } else {
            // root === parent
            addTag(['E', pe.id])
            addTag(['K', `${pe.kind}`])
            addTag(['P', pe.pubkey])
          }
        } else {
          // NIP-10
          if (rootInfo && 'identifier' in rootInfo) {
            addTag([
              'a',
              `${rootInfo.kind}:${rootInfo.pubkey}:${rootInfo.identifier}`,
              ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : []),
              'root'
            ])
            addMention(rootInfo.pubkey)
          } else if (rootInfo && 'id' in rootInfo) {
            addTag([
              'e',
              rootInfo.id,
              ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : []),
              'root',
              ...(rootInfo.author ? [rootInfo.author] : [])
            ])
          } else {
            // root === parent (ignore external)
            addTag(['e', pe.id, client.getEventHint(pe.id)])
          }
        }
      }

      // parent tags
      for (const pe of parentEvents) {
        if (shouldPublishAsComment) {
          // NIP-22
          tags.push(
            ...[
              isReplaceableEvent(pe.kind)
                ? trimTagEnd([
                    'a',
                    getReplaceableCoordinateFromEvent(pe),
                    client.getEventHint(pe.id)
                  ])
                : trimTagEnd(['e', pe.id, client.getEventHint(pe.id), pe.pubkey]),
              ['k', `${pe.kind}`]
            ]
          )
        } else {
          // NIP-10
          tags.push(
            ...[
              isReplaceableEvent(pe.kind)
                ? ['a', getReplaceableCoordinateFromEvent(pe), client.getEventHint(pe.id), 'reply']
                : ['e', pe.id, client.getEventHint(pe.id), 'reply', pe.pubkey]
            ]
          )
        }
        addMention(pe.pubkey)
      }
    } else if (parentExternal) {
      // in this case it will always be NIP-22
      addTag(['I', parentExternal])
      if (parentExternal.startsWith('http')) addTag(['K', 'web'])
      addTag(['i', parentExternal])
      if (parentExternal.startsWith('http')) addTag(['k', 'web'])
    }

    for (const p of additionalMentionPubkeys.values()) addTag(['p', p])
    if (composerState.nsfw) tags.push(['content-warning', 'NSFW'])
    if (composerState.premium) tags.push(['-'], ['nip63'])
    else if (composerState.protected) tags.push(['-'])

    if (!signer) throw new Error('no signer connected')
    const newEvent = await signer.signEvent({
      kind: shouldPublishAsComment ? kinds.Comment : kinds.ShortTextNote,
      content: content,
      tags,
      created_at: scheduledAt ?? now()
    })
    if (!newEvent) throw new Error('sign event returned null')

    const explicitRelayUrls = composerState.relaySelection.flatMap((item) =>
      item.kind === 'relay' ? item.url : item.kind === 'relay-set' ? item.relays : []
    )
    const includeOurWriteRelays =
      scheduledAt == null &&
      composerState.relaySelection.some(
        (item) => item.kind === 'item' && item.id === 'ourWriteRelays'
      )
    const includeTheirReadRelays =
      scheduledAt == null &&
      composerState.relaySelection.some(
        (item) => item.kind === 'item' && item.id === 'theirReadRelays'
      )

    const relayUrls =
      scheduledAt != null && explicitRelayUrls.length > 0
        ? Array.from(new Set(explicitRelayUrls))
        : await client.determineTargetRelays(newEvent, {
            additionalRelayUrls: explicitRelayUrls,
            includeOurWriteRelays,
            includeTheirReadRelays,
            additionalMentionPubkeys
          })

    return [newEvent, relayUrls]
  }

  async function post(e?: React.MouseEvent) {
    e?.stopPropagation()
    if (!canPost) return

    setPosting(true)
    try {
      const [newEvent, relayUrls] = await prepare()

      // persist immediately so closing the tab during the undo window
      // doesn't lose the event; it can be published later from /pending
      await savePendingEvent(newEvent)

      if (editingEvent) {
        discardPendingEvent(editingEvent.id)
      }

      client.addEventToCache(newEvent)
      client.emitEphemeralEvent(newEvent)
      addReplies([newEvent])
      const submittedText = text
      const composerStateSnapshot = composerState
      close()

      const abortController = new AbortController()
      const toastId = `pending-publish-${newEvent.id}`
      const endAt = Date.now() + PENDING_PUBLISH_MS
      const undoPublish = () => {
        abortController.abort()
        void client.removeEventFromCache(newEvent.id)
        void discardPendingEvent(newEvent.id)
        client.emitEventRemoved(newEvent.id)
        ;(isReplyNoteEvent(newEvent) ? invalidateReplies : invalidateFeed)()
        toast.dismiss(toastId)
        removePendingPublish(newEvent.id)
        open(submittedText, composerStateSnapshot)
      }

      addPendingPublish(newEvent.id, endAt, undoPublish)
      removeComposerState(defaultContent, parentEvents, openFromRelays)
      toast(<PendingPublishToast endAt={endAt} onUndo={undoPublish} />, {
        id: toastId,
        duration: PENDING_PUBLISH_MS,
        className: '!bg-transparent !border-none !shadow-none !p-0',
        style: { '--width': 'fit-content' } as React.CSSProperties
      })

      void (async () => {
        try {
          await waitForAbortableDelay(PENDING_PUBLISH_MS, abortController.signal)
          toast.dismiss(toastId)
          removePendingPublish(newEvent.id)
          await client.publishEvent(relayUrls, newEvent)
          if (
            (newEvent.kind === kinds.ShortTextNote || newEvent.kind === kinds.Comment) &&
            recordNotePublishedDay(newEvent.created_at) &&
            hasUnsavedNsec()
          ) {
            openSaveNsecModal()
          }
          await discardPendingEvent(newEvent.id)
          setScheduledAt(null)
          toast.success(t('Published successfully!'), { duration: 2000 })
        } catch (error) {
          toast.dismiss(toastId)
          removePendingPublish(newEvent.id)

          if (abortController.signal.aborted) {
            return
          }

          savePendingEvent(newEvent)
          console.warn('saved as pending')
          toast.error(t('Saved as pending'))
          const errors = error instanceof AggregateError ? error.errors : [error]
          for (const err of errors) {
            toast.error(
              `${t('Failed to post')}: ${err instanceof Error ? err.message : String(err)}`,
              { duration: 10_000 }
            )
            console.error(err)
          }
        }
      })()
    } catch (error) {
      const errors = error instanceof AggregateError ? error.errors : [error]
      for (const err of errors) {
        toast.error(`${t('Failed to post')}: ${err instanceof Error ? err.message : String(err)}`, {
          duration: 10_000
        })
        console.error(err)
      }
      return
    } finally {
      setPosting(false)
    }
  }

  async function saveForLater(e?: React.MouseEvent) {
    e?.stopPropagation()
    if (!canPost) return

    setPosting(true)
    try {
      const [newEvent, _] = await prepare()

      if (editingEvent) {
        discardPendingEvent(editingEvent.id)
      }

      savePendingEvent(newEvent)
      close()
      toast.success(t('Saved for later'))
    } catch (error) {
      console.warn('failed to save draft:', error instanceof Error ? error.message : String(error))
      toast.error(
        `${t('Failed to save draft')}: ${error instanceof Error ? error.message : String(error)}`
      )
    } finally {
      setPosting(false)
    }
  }

  function handleUploadStart(file: UploadFile, cancel: () => void) {
    setUploadProgresses((prev) => {
      const cleaned = prev.filter((item) => {
        if (item.file._uploadId === file._uploadId) return false
        if (item.file.name !== file.name) return true
        return !item.servers.every((s) => s.status.state === 'error')
      })
      return [...cleaned, { file, cancel, servers: [] }]
    })
  }

  function handleUploadInit(info: { file: UploadFile; cancel: () => void; servers: string[] }) {
    setUploadProgresses((prev) =>
      prev.map((item) =>
        item.file._uploadId === info.file._uploadId
          ? {
              ...item,
              servers: info.servers.map((url) => ({
                url,
                status: { state: 'pending' } as ServerStatus
              }))
            }
          : item
      )
    )
  }

  function handleServerStatus(file: UploadFile, server: string, status: ServerStatus) {
    setUploadProgresses((prev) =>
      prev.map((item) =>
        item.file._uploadId === file._uploadId
          ? {
              ...item,
              servers: item.servers.map((s) => (s.url === server ? { ...s, status } : s))
            }
          : item
      )
    )
  }

  function handleUploadEnd(file: UploadFile) {
    setUploadProgresses((prev) => prev.filter((item) => item.file._uploadId !== file._uploadId))
  }
}
